import { BodyText, Field, FieldError, Input, Search } from "@/components/ui";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { zodResolver } from "@hookform/resolvers/zod";
import type * as fhirTypes from "fhir/r4";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { useSnomedSearch } from "../network/useSnomedSearch";
import { editObservationSchema } from "./schemas";

// snomed search expression for clinical findings (observations)
const CLINICAL_FINDING = "<< 404684003";

interface ObservationEditorProps {
  onSubmit(v: z.infer<typeof editObservationSchema>): void;
  onCancel(): void;
  isLoading: boolean;
  observation?: fhirTypes.Observation | null;
  observationCode?: fhirTypes.CodeableConcept;
}

export const ObservationEditor = ({
  observation,
  observationCode,
  onCancel,
  onSubmit,
  isLoading,
}: ObservationEditorProps) => {
  const [input, setInput] = useState("");

  const { data, isLoading: isLoadingSnomed } = useSnomedSearch({
    searchTerm: input,
    eclExpression: CLINICAL_FINDING,
  });

  const form = useForm<z.infer<typeof editObservationSchema>>({
    resolver: zodResolver(editObservationSchema),
    mode: "onSubmit",
    defaultValues: {
      code: observation?.code || observationCode,
      observationValue: observation?.valueString || "",
    },
  });

  const options = useMemo(() => {
    return data?.codes?.map((code) => ({
      label: code.display,
      value: code,
    }));
  }, [data?.codes]);

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="w-full flex flex-col gap-2 pl-3"
      data-testid="edit-observable-form"
    >
      <div className="grid grid-cols-2 gap-1">
        {observation || observationCode ? (
          <BodyText bold size="sm" className="col-span-2">
            {observation?.code.text ||
              observation?.code.coding?.[0].display ||
              observationCode?.text ||
              observationCode?.coding?.[0].display}
          </BodyText>
        ) : (
          <Controller
            control={form.control}
            name="code"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Search
                  disabled={Boolean(observationCode) || Boolean(observation)}
                  isLoading={isLoading || isLoadingSnomed}
                  autoFocusInput
                  data-testid="edit-observable-search"
                  placeholder="Search for a clinical finding"
                  options={options || []}
                  onInputChange={setInput}
                  onItemSelected={(codableConcept) => {
                    field.onChange({ coding: [codableConcept.value] });
                  }}
                  input={field.value?.text || field.value?.coding?.[0].display}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        )}

        <Controller
          control={form.control}
          name="observationValue"
          render={({ field, fieldState }) => (
            <Field
              data-invalid={fieldState.invalid}
              className={
                observation || observationCode ? "col-span-2" : undefined
              }
            >
              <Input
                id="observationValue"
                placeholder="Enter value"
                data-testid="edit-observable-input"
                autoFocus={Boolean(observation || observationCode)}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>
      <SubmitCancelButtons isSaving={isLoading} onCancel={onCancel} />
    </form>
  );
};
